import React, { useState, useMemo, useEffect } from 'react';
import { recruitmentTheme, themeClasses } from '../styles/theme';
import { mockCandidates, mockJobs, mockAppointments } from '../data/mockData';
import { subscribeToTasks, Task as FirebaseTask } from '../services/taskService';
import { BarChart3, TrendingUp, Users, Briefcase, Calendar, Download, FileText, ArrowUpDown, Search, CheckSquare, Clock } from 'lucide-react';
import { enhancedSearch } from '../utils/searchUtils';
import { LocalStorageManager } from '../utils/localStorage';
import { ReportData } from '../services/reportService';
import { subscribeToCandidates } from '../services/candidateService';
import { subscribeToJobs } from '../services/jobService';
import { subscribeToAppointments } from '../services/appointmentService';

interface Candidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  stage: string;
  salary?: number;
  jobTitle?: string;
  countryApplyingTo?: string;
  createdAt?: string;
  lastContacted?: string;
  [key: string]: any;
}

const ReportsPage: React.FC = () => {
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf' | 'excel'>('csv');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedTaskAssignee, setSelectedTaskAssignee] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [liveTasks, setLiveTasks] = useState<FirebaseTask[]>([]);

  // Setup Firebase subscriptions
  useEffect(() => {
    setIsLoading(true);
    
    let unsubscribeCandidates: (() => void) | null = null;
    let unsubscribeJobs: (() => void) | null = null;
    let unsubscribeAppointments: (() => void) | null = null;
    let unsubscribeTasks: (() => void) | null = null;
    
    const setupSubscriptions = async () => {
      try {
        // Subscribe to candidates
        unsubscribeCandidates = await subscribeToCandidates((firebaseCandidates) => {
          console.log('📊 Reports: Received', firebaseCandidates.length, 'candidates from Firebase');
          const mappedCandidates = firebaseCandidates.map(c => ({
            ...c,
            id: c.id || c._id || '',
          }));
          setCandidates(mappedCandidates);
          setIsLoading(false);
        }, console.error);
        
        // Subscribe to jobs
        unsubscribeJobs = subscribeToJobs((firebaseJobs) => {
          console.log('📊 Reports: Received', firebaseJobs.length, 'jobs from Firebase');
          setJobs(firebaseJobs);
        });
        
        // Subscribe to appointments
        unsubscribeAppointments = subscribeToAppointments((firebaseAppointments) => {
          console.log('📊 Reports: Received', firebaseAppointments.length, 'appointments from Firebase');
          setAppointments(firebaseAppointments);
        });
        
        // Subscribe to tasks
        unsubscribeTasks = await subscribeToTasks((firebaseTasks) => {
          console.log('📊 Reports: Received', firebaseTasks.length, 'tasks from Firebase');
          setLiveTasks(firebaseTasks);
          setLastUpdate(Date.now());
        }, console.error);
      } catch (error) {
        console.error('Error loading initial data in Reports:', error);
        setCandidates([]);
        setIsLoading(false);
      }
    };

    setupSubscriptions();


    // Set up real-time event listeners for immediate updates
    const handleCandidatesUpdate = (event: CustomEvent) => {
      console.log('🔄 Reports: Candidates updated via event', event.detail);
      if (event.detail.candidates) {
        setCandidates(event.detail.candidates.map((c: any) => ({ ...c, id: c.id || c._id })));
      }
    };

    const handleAppointmentsUpdate = (event: CustomEvent) => {
      console.log('🔄 Reports: Appointments updated via event', event.detail);
      // Force recalculation by updating timestamp
      setLastUpdate(Date.now());
    };

    const handleJobsUpdate = (event: CustomEvent) => {
      console.log('🔄 Reports: Jobs updated via event', event.detail);
      // Force recalculation by updating timestamp
      setLastUpdate(Date.now());
    };

    const handleTasksUpdate = (event: CustomEvent) => {
      console.log('🔄 Reports: Tasks updated via event', event.detail);
      // Force recalculation by updating timestamp
      setLastUpdate(Date.now());
    };

    // Add event listeners
    window.addEventListener('candidatesUpdated', handleCandidatesUpdate as EventListener);
    window.addEventListener('appointmentsUpdated', handleAppointmentsUpdate as EventListener);
    window.addEventListener('jobsUpdated', handleJobsUpdate as EventListener);
    window.addEventListener('tasksUpdated', handleTasksUpdate as EventListener);

    // Listen for localStorage changes to update reports in real-time
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.startsWith('recruitment_crm_')) {
        // Only respond to LocalStorageManager storage changes
        console.log('🔄 Reports: LocalStorageManager data changed for key:', e.key);
        // Firebase subscriptions will handle updates
        setLastUpdate(Date.now());
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      // Clean up all Firebase subscriptions
      console.log('📊 Reports: Cleaning up Firebase subscriptions...');
      if (unsubscribeCandidates) {
        unsubscribeCandidates();
      }
      if (unsubscribeJobs) {
        unsubscribeJobs();
      }
      if (unsubscribeAppointments) {
        unsubscribeAppointments();
      }
      if (unsubscribeTasks) {
        unsubscribeTasks();
      }
      // Clean up event listeners
      window.removeEventListener('candidatesUpdated', handleCandidatesUpdate as EventListener);
      window.removeEventListener('appointmentsUpdated', handleAppointmentsUpdate as EventListener);
      window.removeEventListener('jobsUpdated', handleJobsUpdate as EventListener);
      window.removeEventListener('tasksUpdated', handleTasksUpdate as EventListener);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Calculate live statistics from combined data
  const stats = useMemo(() => {
    // Always use the latest candidates data for live sync - prefer actual candidates over mock data
    const currentCandidates: any[] = candidates.length > 0 ? candidates : [];
    
    // If no candidates loaded, it means no data exists yet (fresh install or data clearing)
    if (currentCandidates.length === 0) {
      console.warn('📊 Reports: No live candidates found, using empty data set');
    }
    
    const candidatesByStage = currentCandidates.reduce((acc: Record<string, number>, candidate: any) => {
      acc[candidate.stage] = (acc[candidate.stage] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const totalCandidates = currentCandidates.length;
    const hiredCandidates = currentCandidates.filter((c: any) => c.stage === 'Hired');
    const activePlacements = currentCandidates.filter((c: any) => c.stage === 'Onboarding' || c.stage === 'Offer').length;
    const completedPlacements = hiredCandidates.length;
    
    // Calculate total revenue from hired candidates (assuming 10% commission)
    const totalRevenue = hiredCandidates.reduce((sum: number, candidate: any) => sum + (candidate.salary || 0) * 0.10, 0);

    // Use Firebase tasks data for live sync
    
    // Use Firebase data for live sync
    const liveJobs = jobs;
    
    return {
      totalCandidates,
      activePlacements,
      completedPlacements,
      revenue: totalRevenue,
      candidatesByStage,
      // Use live data - showing totals to match other pages
      totalAppointments: appointments.length,
      confirmedAppointments: appointments.filter((a: any) => a.status === 'Confirmed' || a.status === 'Scheduled').length,
      completedTasks: liveTasks.filter(t => t.status === 'COMPLETED').length,
      pendingTasks: liveTasks.filter(t => t.status === 'PENDING').length,
      inProgressTasks: liveTasks.filter(t => t.status === 'IN_PROGRESS').length,
      overdueTasks: liveTasks.filter(t => t.status === 'OVERDUE').length,
      totalTasks: liveTasks.length,
      totalJobs: liveJobs.length,  // Show total jobs instead of just active ones
      activeJobs: liveJobs.filter((j: any) => j.status === 'Active').length
    };
  }, [candidates, jobs, appointments, liveTasks, lastUpdate]);

  // Get recent placements from hired candidates
  const recentPlacements = useMemo(() => {
    // Always use the latest candidates data for live sync
    const currentCandidates: any[] = candidates.length > 0 ? candidates : [];
    
    return currentCandidates
      .filter((candidate: any) => candidate.stage === 'Hired')
      .map((candidate: any) => ({
        candidate: candidate.name,
        position: candidate.jobTitle,
        country: candidate.countryApplyingTo,
        fee: candidate.salary * 0.10, // 10% commission
        date: candidate.createdAt || new Date().toISOString()
      }))
      .sort((a: any, b: any) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      })
      .slice(0, 3);
  }, [candidates, lastUpdate]);

  // Export functionality with filtered data
  const handleExport = (format: 'csv' | 'pdf' | 'excel') => {
    const exportData = {
      generatedAt: new Date().toISOString(),
      stats,
      candidates: filteredAndSortedCandidates,
      allCandidates: candidates,
      jobs: LocalStorageManager.getJobs(),
      tasks: LocalStorageManager.getTasks(),
      appointments: LocalStorageManager.getAppointments(),
      placements: recentPlacements,
      filters: {
        selectedStage,
        selectedCountry,
        selectedTaskAssignee,
        searchTerm
      },
      reportData
    };

    switch (format) {
      case 'csv':
        exportToCSV(exportData);
        break;
      case 'pdf':
        exportToPDF(exportData);
        break;
      case 'excel':
        exportToExcel(exportData);
        break;
    }
  };

  // Print functionality
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const printContent = generatePrintContent();
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const generatePrintContent = () => {
    const filterInfo = [];
    if (selectedStage) filterInfo.push(`Stage: ${selectedStage}`);
    if (selectedCountry) filterInfo.push(`Country: ${selectedCountry}`);
    if (selectedTaskAssignee) filterInfo.push(`Task Assignee: ${selectedTaskAssignee}`);
    if (searchTerm) filterInfo.push(`Search: ${searchTerm}`);
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Recruitment CRM Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .filters { background: #f5f5f5; padding: 10px; margin: 20px 0; }
          .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin: 20px 0; }
          .stat-card { text-align: center; padding: 15px; border: 1px solid #ddd; }
          .table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          .table th { background-color: #f2f2f2; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Recruitment CRM Report</h1>
          <p>Generated: ${new Date().toLocaleString()}</p>
          ${filterInfo.length > 0 ? `<p>Filters Applied: ${filterInfo.join(', ')}</p>` : ''}
        </div>
        
        <div class="stats">
          <div class="stat-card">
            <h3>Total Candidates</h3>
            <p>${stats.totalCandidates}</p>
          </div>
          <div class="stat-card">
            <h3>Active Placements</h3>
            <p>${stats.activePlacements}</p>
          </div>
          <div class="stat-card">
            <h3>Completed Placements</h3>
            <p>${stats.completedPlacements}</p>
          </div>
          <div class="stat-card">
            <h3>Total Revenue</h3>
            <p>KES ${stats.revenue.toLocaleString()}</p>
          </div>
        </div>
        
        <h2>Candidate Details</h2>
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Position</th>
              <th>Country</th>
              <th>Stage</th>
              <th>Salary</th>
              <th>Date</th>
              <th>Departure</th>
            </tr>
          </thead>
          <tbody>
            ${filteredAndSortedCandidates.map(candidate => `
              <tr>
                <td>${candidate.name}</td>
                <td>${candidate.jobTitle || 'N/A'}</td>
                <td>${candidate.countryApplyingTo || 'N/A'}</td>
                <td>${candidate.stage}</td>
                <td>KES ${(candidate.salary || 0).toLocaleString()}</td>
                <td>${candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString() : 'N/A'}</td>
                <td>${((candidate as any).departureDate && (candidate as any).departureTime) ? `${new Date((candidate as any).departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} at ${(candidate as any).departureTime}` : 'Not set'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        ${reportData?.taskPerformance ? `
          <h2>Task Performance</h2>
          <table class="table">
            <thead>
              <tr>
                <th>Assignee</th>
                <th>Total Tasks</th>
                <th>Completed</th>
                <th>Pending</th>
                <th>Completion Rate</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.taskPerformance.map((assignee: any) => `
                <tr>
                  <td>${assignee.assignedTo}</td>
                  <td>${assignee.totalTasks}</td>
                  <td>${assignee.completedTasks}</td>
                  <td>${assignee.pendingTasks}</td>
                  <td>${Math.round(assignee.completionRate)}%</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : ''}
      </body>
      </html>
    `;
  };

  const exportToCSV = (data: any) => {
    const filterInfo = [];
    if (data.filters.selectedStage) filterInfo.push(`Stage: ${data.filters.selectedStage}`);
    if (data.filters.selectedCountry) filterInfo.push(`Country: ${data.filters.selectedCountry}`);
    if (data.filters.selectedTaskAssignee) filterInfo.push(`Task Assignee: ${data.filters.selectedTaskAssignee}`);
    if (data.filters.searchTerm) filterInfo.push(`Search: ${data.filters.searchTerm}`);
    
    const csvContent = `
Report Generated: ${new Date().toLocaleString()}
${filterInfo.length > 0 ? `Filters Applied: ${filterInfo.join(', ')}\n` : ''}

SUMMARY STATISTICS
Total Candidates,${data.stats.totalCandidates}
Active Placements,${data.stats.activePlacements}
Completed Placements,${data.stats.completedPlacements}
Total Revenue (KES),${data.stats.revenue}
Filtered Results,${data.candidates.length}

FILTERED CANDIDATES
Name,Position,Country,Stage,Salary (KES),Date,Departure
${data.candidates.map((c: any) => `${c.name},${c.jobTitle || 'N/A'},${c.countryApplyingTo || 'N/A'},${c.stage},${(c.salary || 0).toLocaleString()},${new Date(c.createdAt).toLocaleDateString()},${(c.departureDate && c.departureTime) ? `${new Date(c.departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} at ${c.departureTime}` : 'Not set'}`).join('\n')}

RECENT PLACEMENTS
Candidate,Position,Country,Salary (KES)
${data.placements.map((p: any) => `${p.candidate},${p.position},${p.country},${p.fee}`).join('\n')}

${data.reportData?.taskPerformance ? `TASK PERFORMANCE
Assignee,Total Tasks,Completed,Pending,Completion Rate
${data.reportData.taskPerformance.map((t: any) => `${t.assignedTo},${t.totalTasks},${t.completedTasks},${t.pendingTasks},${Math.round(t.completionRate)}%`).join('\n')}` : ''}
`.trim();

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recruitment-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    alert('CSV report exported successfully!');
  };

  const exportToPDF = (data: any) => {
    const filterInfo = [];
    if (data.filters.selectedStage) filterInfo.push(`Stage: ${data.filters.selectedStage}`);
    if (data.filters.selectedCountry) filterInfo.push(`Country: ${data.filters.selectedCountry}`);
    if (data.filters.selectedTaskAssignee) filterInfo.push(`Task Assignee: ${data.filters.selectedTaskAssignee}`);
    if (data.filters.searchTerm) filterInfo.push(`Search: ${data.filters.searchTerm}`);
    
    // Create a comprehensive PDF report
    const pdfContent = `
RECRUITMENT CRM REPORT
Generated: ${new Date().toLocaleString()}
${filterInfo.length > 0 ? `Filters Applied: ${filterInfo.join(', ')}\n` : ''}

=== SUMMARY STATISTICS ===
Total Candidates: ${data.stats.totalCandidates}
Active Placements: ${data.stats.activePlacements}
Completed Placements: ${data.stats.completedPlacements}
Total Revenue: KES ${data.stats.revenue.toLocaleString()}
Filtered Results: ${data.candidates.length}

=== FILTERED CANDIDATES ===
${data.candidates.map((c: any) => `${c.name} - ${c.jobTitle || 'N/A'} (${c.countryApplyingTo || 'N/A'}) - ${c.stage} - KES ${(c.salary || 0).toLocaleString()} - Departure: ${(c.departureDate && c.departureTime) ? `${new Date(c.departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} at ${c.departureTime}` : 'Not set'}`).join('\n')}

=== CANDIDATES BY STAGE ===
${Object.entries(data.stats.candidatesByStage).map(([stage, count]) => `${stage}: ${count}`).join('\n')}

${data.reportData?.taskPerformance ? `=== TASK PERFORMANCE ===
${data.reportData.taskPerformance.map((t: any) => `${t.assignedTo}: ${t.completedTasks}/${t.totalTasks} tasks (${Math.round(t.completionRate)}%)`).join('\n')}
` : ''}
=== APPOINTMENTS ===
Total Appointments: ${data.stats.totalAppointments}
Confirmed Appointments: ${data.stats.confirmedAppointments}

=== RECENT PLACEMENTS ===
${data.placements.map((p: any) => `${p.candidate} - ${p.position} (${p.country}) - KES ${p.fee.toLocaleString()}`).join('\n')}
`;
    
    // Create and download text file (in real app, would use jsPDF)
    const blob = new Blob([pdfContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recruitment-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
    alert('PDF report exported successfully!');
  };

  const exportToExcel = (data: any) => {
    // In a real application, you would use a library like SheetJS
    alert('Excel export functionality would be implemented with SheetJS library.');
  };

  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  // Filter and sort candidates for the placement history table
  const filteredAndSortedCandidates = useMemo(() => {
    let filtered = candidates;
    
    // Apply stage filter
    if (selectedStage) {
      filtered = filtered.filter(candidate => candidate.stage === selectedStage);
    }
    
    // Apply country filter
    if (selectedCountry) {
      filtered = filtered.filter(candidate => candidate.countryApplyingTo === selectedCountry);
    }
    
    // Apply search filter using enhanced search
    if (searchTerm) {
      filtered = filtered.filter(candidate => 
        enhancedSearch(searchTerm, [
          candidate.name,
          candidate.jobTitle,
          candidate.countryApplyingTo,
          candidate.stage,
          candidate.email,
          candidate.company,
          candidate.phone,
          candidate.passportNumber,
          candidate.salary?.toString(),
          candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString() : 'N/A'
        ])
      );
    }
    
    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortField) {
          case 'date':
            aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            break;
          case 'salary':
            aValue = a.salary || 0;
            bValue = b.salary || 0;
            break;
          default:
            aValue = a[sortField as keyof typeof a];
            bValue = b[sortField as keyof typeof b];
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }
    
    return filtered.slice(0, 20); // Show up to 20 results
  }, [candidates, selectedStage, selectedCountry, searchTerm, sortField, sortDirection]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>
            Reports & Analytics
          </h1>
          <p className="text-gray-600 mt-1">
            Track performance and analyze recruitment metrics
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search reports data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${recruitmentTheme.components.input} pl-10 w-80`}
            />
          </div>
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as 'csv' | 'pdf' | 'excel')}
            className={recruitmentTheme.components.select}
          >
            <option value="csv">CSV Format</option>
            <option value="pdf">PDF Format</option>
            <option value="excel">Excel Format</option>
          </select>
          <button 
            onClick={() => handleExport(exportFormat)}
            className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
          >
            <Download className="w-4 h-4" />
            <span>Export Report</span>
          </button>
          <button 
            onClick={handlePrint}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
          >
            <FileText className="w-4 h-4" />
            <span>Print Report</span>
          </button>
        </div>
      </div>

      {/* Key Metrics - Synced with Other Pages */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Candidates</p>
              <p className="text-2xl font-bold text-[#8b6914]">{stats.totalCandidates}</p>
            </div>
            <Users className="w-8 h-8 text-[#8b6914]" />
          </div>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold text-[#8b6914]">{stats.totalJobs}</p>
            </div>
            <Briefcase className="w-8 h-8 text-[#8b6914]" />
          </div>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Tasks</p>
              <p className="text-2xl font-bold text-[#8b6914]">{stats.completedTasks + stats.pendingTasks + stats.inProgressTasks + stats.overdueTasks}</p>
            </div>
            <CheckSquare className="w-8 h-8 text-[#8b6914]" />
          </div>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed Tasks</p>
              <p className="text-2xl font-bold text-green-600">{stats.completedTasks}</p>
            </div>
            <CheckSquare className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Appointments</p>
              <p className="text-2xl font-bold text-[#8b6914]">{stats.totalAppointments}</p>
            </div>
            <Calendar className="w-8 h-8 text-[#8b6914]" />
          </div>
        </div>

        <div className={`${recruitmentTheme.components.card} ${recruitmentTheme.animations.scaleIn}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Tasks</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingTasks}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
      </div>

      {/* Candidates by Stage - Synced with Other Pages */}
      <div className={recruitmentTheme.components.card}>
        <h2 className={`${themeClasses.sectionTitle} mb-4`}>Candidates by Stage</h2>
        <p className="text-sm text-gray-600 mb-4">Real-time data synced across all pages</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Object.entries(stats.candidatesByStage).map(([stage, count]) => (
            <div key={stage} className="text-center p-4 bg-cream-warm rounded-lg border border-gray-200">
              <div className="text-2xl font-bold text-[#8b6914] mb-1">{count as number}</div>
              <div className="text-sm font-medium text-[#8b6914]">{stage}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Placements */}
        <div className={recruitmentTheme.components.card}>
          <h2 className={`${themeClasses.sectionTitle} mb-4`}>Recent Successful Placements</h2>
          <div className="space-y-3">
            {recentPlacements.map((placement: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 bg-cream-warm rounded-lg">
                <div>
                  <p className="font-medium text-[#8b6914]">{placement.candidate}</p>
                  <p className="text-sm text-gray-600">{placement.position} • {placement.country}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-[#8b6914]">
                    {new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(placement.fee)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live Performance Dashboard */}
        <div className={recruitmentTheme.components.card}>
          <h2 className={`${themeClasses.sectionTitle} mb-4`}>Live Performance Dashboard</h2>
          <div className="space-y-4">
            {/* Stage Distribution - Interactive */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Candidate Stage Distribution</h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(
                  (candidates as any[]).reduce((acc: any, candidate: any) => {
                    acc[candidate.stage] = (acc[candidate.stage] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([stage, count]) => (
                  <div 
                    key={stage} 
                    className={`flex items-center justify-between p-2 rounded cursor-pointer hover:shadow-md transition-all ${
                      selectedStage === stage 
                        ? 'bg-[#8b6914] text-white' 
                        : 'bg-cream-warm hover:bg-cream-light'
                    }`}
                    onClick={() => setSelectedStage(selectedStage === stage ? null : stage)}
                  >
                    <span className="text-sm">{stage}</span>
                    <span className="text-sm font-semibold">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Country Distribution - Interactive */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Applications by Country</h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(
                  candidates.reduce((acc: any, candidate: any) => {
                    const country = candidate.countryApplyingTo || 'Unknown';
                    acc[country] = (acc[country] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([country, count]) => (
                  <div 
                    key={country} 
                    className={`flex items-center justify-between p-2 rounded cursor-pointer hover:shadow-md transition-all ${
                      selectedCountry === country 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-50 hover:bg-blue-100'
                    }`}
                    onClick={() => setSelectedCountry(selectedCountry === country ? null : country)}
                  >
                    <span className="text-sm">{country}</span>
                    <span className="text-sm font-semibold">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Task Performance - Interactive */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Task Performance by Assignee</h3>
              <div className="grid grid-cols-2 gap-3">
                {reportData?.taskPerformance?.map((assignee: any) => (
                  <div 
                    key={assignee.assignedTo}
                    className={`flex items-center justify-between p-2 rounded cursor-pointer hover:shadow-md transition-all ${
                      selectedTaskAssignee === assignee.assignedTo 
                        ? 'bg-green-600 text-white' 
                        : 'bg-green-50 hover:bg-green-100'
                    }`}
                    onClick={() => setSelectedTaskAssignee(selectedTaskAssignee === assignee.assignedTo ? null : assignee.assignedTo)}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{assignee.assignedTo}</span>
                      <span className="text-xs opacity-75">{assignee.completedTasks}/{assignee.totalTasks} tasks</span>
                    </div>
                    <span className="text-sm font-semibold">{Math.round(assignee.completionRate)}%</span>
                  </div>
                )) || (
                  <div className="col-span-2 space-y-2">
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                      <span className="text-sm text-gray-600">Completed</span>
                      <span className="text-sm font-semibold text-green-700">{stats.completedTasks}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                      <span className="text-sm text-gray-600">Pending</span>
                      <span className="text-sm font-semibold text-yellow-700">{stats.pendingTasks}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                      <span className="text-sm text-gray-600">Active Jobs</span>
                      <span className="text-sm font-semibold text-blue-700">{stats.activeJobs}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Reports Table */}
      <div className={recruitmentTheme.components.card}>
        <h2 className={`${themeClasses.sectionTitle} mb-4`}>Placement History</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-[#8b6914]">
                  <button 
                    onClick={() => handleSort('date')}
                    className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                  >
                    <span>Date</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left py-3 px-4 font-medium text-[#8b6914]">
                  <button 
                    onClick={() => handleSort('name')}
                    className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                  >
                    <span>Candidate</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left py-3 px-4 font-medium text-[#8b6914]">
                  <button 
                    onClick={() => handleSort('jobTitle')}
                    className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                  >
                    <span>Position</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left py-3 px-4 font-medium text-[#8b6914]">
                  <button 
                    onClick={() => handleSort('countryApplyingTo')}
                    className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                  >
                    <span>Country</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left py-3 px-4 font-medium text-[#8b6914]">
                  <button 
                    onClick={() => handleSort('salary')}
                    className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                  >
                    <span>Salary</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left py-3 px-4 font-medium text-[#8b6914]">
                  <button 
                    onClick={() => handleSort('stage')}
                    className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                  >
                    <span>Status</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left py-3 px-4 font-medium text-[#8b6914]">
                  <button 
                    onClick={() => handleSort('departureDate')}
                    className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                  >
                    <span>Departure</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedCandidates.map((candidate, index) => (
                <tr key={candidate._id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-600">
                    {candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                        {index + 1}.
                      </span>
                      <div className="text-[#8b6914] font-medium">{candidate.name}</div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-600">{candidate.jobTitle || 'N/A'}</td>
                  <td className="py-3 px-4 text-gray-600">{candidate.countryApplyingTo || 'N/A'}</td>
                  <td className="py-3 px-4 text-gray-600">
                    {new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(candidate.salary || 0)}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      candidate.stage === 'Hired' ? 'bg-green-100 text-green-800' :
                      candidate.stage === 'Offer' ? 'bg-blue-100 text-blue-800' :
                      candidate.stage === 'Onboarding' ? 'bg-yellow-100 text-yellow-800' :
                      candidate.stage === 'Interviewing' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {candidate.stage}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {((candidate as any).departureDate && (candidate as any).departureTime) 
                      ? `${new Date((candidate as any).departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} at ${(candidate as any).departureTime}`
                      : 'Not set'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredAndSortedCandidates.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-[#8b6914] opacity-50" />
            <h3 className="mt-2 text-sm font-medium text-[#8b6914]">No placement data found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedStage || selectedCountry ? 'Try adjusting your search or filter criteria.' : 'Placement history will appear here as candidates are processed.'}
            </p>
          </div>
        )}
        
        {/* Filter Summary */}
        {(selectedStage || selectedCountry) && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2 text-sm text-blue-700">
              <span>Active filters:</span>
              {selectedStage && (
                <span className="px-2 py-1 bg-blue-100 rounded-full text-xs">
                  Stage: {selectedStage}
                  <button 
                    onClick={() => setSelectedStage(null)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedCountry && (
                <span className="px-2 py-1 bg-blue-100 rounded-full text-xs">
                  Country: {selectedCountry}
                  <button 
                    onClick={() => setSelectedCountry(null)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              <button 
                onClick={() => {
                  setSelectedStage(null);
                  setSelectedCountry(null);
                  setSelectedTaskAssignee(null);
                }}
                className="text-blue-600 hover:text-blue-800 text-xs underline"
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;